import { Link, useNavigate } from '@tanstack/react-router';
import { Keyboard, Link2, LogOut, Moon, Plus, Repeat, Search, Sun, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { Avatar } from '../components/Avatar';
import { Keys, MOD_KEY } from '../components/Kbd';
import { NestRecruitLogo } from '../components/NestRecruitLogo';
import { MenuGroup, MenuItem, MenuSeparator, Popover, usePopover } from '../components/Popover';
import { RECENT_RECORD_KIND_LABELS, isDelegationOn } from '@nest-recruit/shared';
import { useRecents } from '../stores/recents';
import { roleLabelsOf, useCanEdit, useCanView, useRequiredSession } from '../stores/session';
import { useUi } from '../stores/ui';
import { useLogout } from '../features/auth/api/auth-api';
import { NotificationBell } from './NotificationBell';
import { ScopeSwitcher } from './ScopeSwitcher';
import styles from './UtilityBar.module.css';
import { useModule, useModules } from '../lib/modules';

// Level 1 of the shell. Mounted once and never re-rendered by navigation
// (CLAUDE.md §5a rule 6).

export function UtilityBar(): ReactNode {
  const openPalette = useUi((state) => state.openPalette);
  const toggleShortcutSheet = useUi((state) => state.toggleShortcutSheet);
  const theme = useUi((state) => state.theme);
  const toggleTheme = useUi((state) => state.toggleTheme);
  // S10: a hiring manager's one thing to create is on their own bar (Request a hire).
  const isHiringManager = useRequiredSession().isHiringManagerSurface;
  const isRequestsOn = useModule('requisitions');

  return (
    <div className={styles.bar}>
      <a className={styles.wordmark} href="/" aria-label="Nest Recruit, go to dashboard">
        <NestRecruitLogo className={styles.mark} />
        <span className={styles.wordmarkText}>Nest Recruit</span>
      </a>

      <ScopeSwitcher />

      {/* F20.04: opens the palette, which searches records from two letters (S25 Q2) —
          one search surface rather than two to keep in step. */}
      <button
        type="button"
        className={styles.search}
        onClick={openPalette}
        aria-label="Search everything"
      >
        <Search size={15} aria-hidden />
        <span className={styles.searchLabel}>{isRequestsOn ? 'Search candidates, jobs, requisitions' : 'Search candidates and jobs'}</span>
        <span className={styles.searchKeys} aria-hidden>
          <Keys keys={[MOD_KEY, 'K']} />
        </span>
      </button>

      <span className={styles.spacer} />

      {/* Evening review 2026-11-01 item 13: All candidates left the job's More menu (it isn't a step of the hiring flow) and
          sits here, beside Create, opening its own page. */}
      {!isHiringManager && <CandidatesButton />}
      {!isHiringManager && <QuickCreate />}

      <NotificationBell />

      <button
        type="button"
        className={`${styles.iconButton} ${styles.themeToggle}`}
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? <Sun size={17} aria-hidden /> : <Moon size={17} aria-hidden />}
      </button>

      <AvatarMenu onOpenShortcuts={toggleShortcutSheet} />
    </div>
  );
}

/** The avatar menu shows the five most recent; the palette has all ten (06-qol §1). */
const AVATAR_RECENTS = 5;

function CandidatesButton(): ReactNode {
  if (!useCanView('candidate')) return null;
  return (
    <Link to="/candidates" className={styles.candidates} aria-label="All candidates">
      <Users size={16} aria-hidden />
      <span className={styles.candidatesLabel}>Candidates</span>
    </Link>
  );
}

function QuickCreate(): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();

  const modules = useModules();
  const canRequisition = useCanEdit('requisition') && modules.requisitions;
  const canJob = useCanEdit('job_config');
  const canCandidate = useCanEdit('candidate');
  const canInterview = useCanEdit('interview');
  // Hrefs, so open-in-new-tab works. A job is created directly (F5.24, R1) and an interview
  // from the list waiting to be scheduled (S32).
  const items = [
    ...(canRequisition ? [{ label: 'Requisition', href: '/requisitions/new' }] : []),
    ...(canJob ? [{ label: 'Job', href: '/jobs/new' }] : []),
    ...(canCandidate ? [{ label: 'Candidate', href: '/candidates/new' }] : []),
    ...(canInterview ? [{ label: 'Interview, for someone waiting', href: '/interviews/to-schedule' }] : []),
  ];
  if (items.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.iconButton} ${styles.create}`}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Create"
      >
        <Plus size={18} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} align="right" label="Create">
        <MenuGroup label="Create">
          {items.map((item) => (
            <MenuItem key={item.href} href={item.href} onClick={close}>
              {item.label}
            </MenuItem>
          ))}
        </MenuGroup>
      </Popover>
    </>
  );
}

function AvatarMenu({ onOpenShortcuts }: { onOpenShortcuts: () => void }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const session = useRequiredSession();
  const theme = useUi((state) => state.theme);
  const toggleTheme = useUi((state) => state.toggleTheme);
  const navigate = useNavigate();
  const logout = useLogout();
  const recents = useRecents((state) => state.records);
  const isCareerSiteOn = useModule('career_site');
  // Cover follows Hire requests, not Approval chains: approvals run without the chains (brief 2026-11-02 §7).
  // Review R18: cover moves hire-request and offer approvals, so it is offered while either part is on.
  const isCoverOn = isDelegationOn(useModules());

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.avatarButton}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`Account menu for ${session.fullName}`}
      >
        <Avatar name={session.fullName} size="lg" />
        <span className={styles.identity}>
          <span className={styles.identityName}>{session.fullName}</span>
          <span className={styles.identityRole}>{roleLabelsOf(session)}</span>
        </span>
      </button>

      <Popover anchor={anchor} onClose={close} align="right" label="Account">
        {recents.length > 0 && (
          <>
            <MenuGroup label="Recently viewed">
              {recents.slice(0, AVATAR_RECENTS).map((record) => (
                <MenuItem key={record.id} href={record.path} meta={RECENT_RECORD_KIND_LABELS[record.kind]} onClick={close}>
                  {record.title}
                </MenuItem>
              ))}
            </MenuGroup>
            <MenuSeparator />
          </>
        )}
        <MenuGroup label={session.email}>
          <MenuItem
            icon={<Keyboard size={15} />}
            meta={<Keys keys={['?']} />}
            onClick={() => {
              close();
              onOpenShortcuts();
            }}
          >
            Keyboard shortcuts
          </MenuItem>
          {/* At mobile the theme toggle leaves the bar so it never wraps; it
              lives here instead so it is always reachable. */}
          {/* F6.05: any staff member can refer someone to an open job — with Career site on. */}
          {isCareerSiteOn && (
            <MenuItem
              icon={<Link2 size={15} />}
              onClick={() => {
                close();
                void navigate({ to: '/referrals' });
              }}
            >
              Refer someone
            </MenuItem>
          )}
          {/* F2.11: everyone arranges their own cover, not only HR (tester D28). */}
          {isCoverOn && (
            <MenuItem
              icon={<Repeat size={15} />}
              onClick={() => {
                close();
                void navigate({ to: '/admin/delegation' });
              }}
            >
              Arrange cover
            </MenuItem>
          )}
          <MenuItem
            icon={theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            onClick={toggleTheme}
          >
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </MenuItem>
        </MenuGroup>

        <MenuSeparator />

        <MenuItem
          icon={<LogOut size={15} />}
          isDanger
          onClick={() => {
            close();
            // A genuine session boundary — one of the three places CLAUDE.md §5a
            // rule 7 permits leaving the single-page app behind. The hook navigates
            // rather than reloading, which keeps even this instant.
            logout.mutate();
          }}
        >
          Sign out
        </MenuItem>
      </Popover>
    </>
  );
}
